<template>
  <model-form
    ref="modelForm"
    :model="itemVariation"
    :submodel="true"
    modal-type="modal"
  >
    <q-card-section>
      <div class="row q-gutter-md">
        <model-form-input
          field="name"
          class="col"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          autofocus
        />
      </div>
      <div class="row q-gutter-md">
        <div class="col-3 q-py-md">
          <model-form-image-uploader
            field="image"
            class="col-4"
            :image-url="itemVariation.imageUrl"
            ratio="1"
          />
        </div>
        <div class="col-3 row justify-center items-center">
          <color-picker
            color-name="custom"
            :color-hex="itemVariation.color"
            :disable="itemVariation.isTranslation"
            @input="value => itemVariation.color = value"
          />
        </div>
      </div>
      <div class="row q-gutter-md">
        <model-form-input
          field="description"
          class="col"
          type="textarea"
          label="Description"
        />
      </div>
      <div class="row q-gutter-md">
        <model-form-input
          field="upcCode"
          class="col"
          label="Bar Code (UPC-A/E / EAN - 8, 12 or 13 digits numeric)"
          type="number"
          :rules="[val => !val || val.length === 8 || val.length === 12 || val.length === 13 || 'Must be 8 or 12 digits long']"
        />
      </div>
      <div class="row q-gutter-md items-start">
        <model-form-input
          :disable="!allowChangingDefaultPrice"
          field="price"
          class="col"
          :label="currentSite.itemPriceLabel"
          :prefix="currentCircuit.localeCurrencySymbol"
          type="number"
          step="0.25"
          :rules="[val => val !== null || 'Field is required']"
        />
        <div class="col">
          <model-form-input
            field="cost"
            :disable="itemVariation.isRemote && itemVariation.disableEditCostCents !== false"
            class="col"
            label="Cost"
            :prefix="currentCircuit.localeCurrencySymbol"
            type="number"
            step="0.01"
          />
          <q-tooltip v-if="itemVariation.isRemote && itemVariation.disableEditCostCents !== false">
            Cost is controlled by the inventory items that are associated through inventory recipes.
          </q-tooltip>
        </div>
      </div>
      <div class="row q-gutter-md items-start">
        <model-form-toggle
          field="overrideSalesChannels"
          class="col-3"
          label="Override Channels"
        />
        <sales-channel-select
          :model="itemVariation.overrideSalesChannels ? itemVariation : itemVariation.item"
          class="col-9"
          :channels="SALES_CHANNEL_TYPES.MENU"
          :disable="!itemVariation.overrideSalesChannels"
        />
      </div>
      <div v-if="currentCircuit.priceTiersEnabled && itemVariation.priceTierPrices.data.length">
        <div class="row text-h6">
          Tier Pricing
        </div>
        <div
          v-if="currentCircuit.priceTiersEnabled"
          class="row q-gutter-md items-start"
        >
          <q-input
            v-for="priceTierPrice in itemVariation.priceTierPrices.data"
            :key="priceTierPrice.priceTier.id"
            v-model="priceTierPrice.price"
            :disable="!allowChangingDefaultPrice"
            class="col-4"
            :label="priceTierPrice.priceTier.name"
            :prefix="currentCircuit.localeCurrencySymbol"
            type="number"
            step="0.25"
            :rules="[val => val !== null || 'Field is required']"
          />
        </div>
      </div>
      <div class="row q-gutter-md q-mb-md">
        <model-form-checkbox
          field="salesDisabled"
          class="col"
          label="Disable Sales"
        />
        <model-form-checkbox
          v-if="itemVariation.hasRecipe"
          field="hasRecipe"
          class="col"
          label="Track Stock"
          disable
        />
        <model-form-checkbox
          v-else-if="currentSite.activeInventoryStrategy && itemVariation.isRemote"
          field="action"
          class="col"
          label="Track Stock"
          :true-value="'manage_inventory'"
          :false-value="null"
        />
      </div>
      <div
        v-if="itemVariation.action === 'manage_inventory'"
        class="row q-gutter-md text-negative"
      >
        <div class="col">
          Important! Marking this sales item variation with "track stock" is
          recommended when a sale should reduce the balance of a <span
            class="text-italic"
          >single</span> inventory item (for example, a can
          of soda or a bag of candy). If a sale should reduce the balances of
          <span class="text-italic">multiple</span> inventory items (for example
          selling a large popcorn might reduce balances for popcorn kernels,
          popcorn salt, oil, large popcorn bags, etc.), we recommend creating
          the inventory items and recipe manually.
        </div>
      </div>
      <availabilities-and-constraints
        v-if="itemVariation"
        :item-variation-or-modifier="itemVariation"
      />
    </q-card-section>
  </model-form>
</template>

<script>
import { TYPES as SALES_CHANNEL_TYPES } from 'src/models/model/concerns/SalesChannelRestrictable'
import ColorPicker from 'src/modules/admin/components/ColorPicker'
import ModelFormCheckbox from 'src/modules/admin/components/ModelForm/components/ModelFormCheckbox'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import SalesChannelSelect from 'src/modules/admin/components/SalesChannelSelect'
import AvailabilitiesAndConstraints from 'src/modules/admin/item/components/AvailabilitiesAndConstraints'

export default {
  name: 'ItemVariationForm',
  components: {
    ModelFormCheckbox,
    ModelForm,
    ColorPicker,
    AvailabilitiesAndConstraints,
    SalesChannelSelect
  },
  configuration: ['currentCircuit', 'currentSite', 'priceTiers'],
  models: {
    itemVariation: {
      inventoryRecipes: true,
      inventoryAvailabilitiesAndConstraintsByLocationAndRegisterStation: true,
      disableEditCostCents: true,
      priceTierPrices: true,
      receiveLibraryUpdates: true,
      libraryParentId: true
    }
  },
  computed: {
    shouldBuildPriceTierPrices () {
      if (this.currentSite.isLibrary && this.currentCircuit?.priceTiersEnabled) {
        return this.itemVariation.priceTierPrices?.data?.length !== this.priceTiers?.data?.length
      }
      return false
    },
    allowChangingDefaultPrice () {
      if (this.itemVariation.isLocal) {
        return true
      }

      if (!this.currentCircuit.priceTiersEnabled) {
        return true
      }

      if (this.currentSite.isLibrary) {
        return true
      }

      if (!this.currentSite.priceTier) {
        return true
      }

      if (!this.itemVariation.receiveLibraryUpdates || !this.itemVariation.libraryParentId) {
        return true
      }

      return false
    }
  },
  watch: {
    shouldBuildPriceTierPrices: {
      handler () {
        if (this.shouldBuildPriceTierPrices) {
          this.buildPriceTierPrices()
        }
      },
      immediate: true
    }
  },
  created () {
    _.extend(this, { SALES_CHANNEL_TYPES })
  },
  methods: {
    buildPriceTierPrices () {
      _.each(this.priceTiers.data, priceTier => {
        if (!_.find(this.itemVariation.priceTierPrices.data, { priceTier })) {
          this.itemVariation.priceTierPrices.add({
            priceTierId: priceTier.id,
            price: this.itemVariation.price || 0
          })
        }
      })
    }
  }
}

</script>
